import React, { useState, useEffect } from 'react';
import { Facebook, Instagram, Linkedin, Youtube } from 'react-feather';
import LogoIcon from '../ui/LogoIcon';
import { trackEvent, trackConversion } from '../../services/trackingService';
import { getBlogPostSummaries } from '../../services/apiService';
import type { BlogPostSummary } from '../../services/apiService';
import { useData } from '../../context/DataContext';
import LocalizedLink from '../ui/LocalizedLink';
import { useTranslation } from 'react-i18next';

const Footer: React.FC = () => {
  const { i18n, t } = useTranslation('common');
  const currentYear = new Date().getFullYear();
  const [recentPosts, setRecentPosts] = useState<BlogPostSummary[]>([]);
  const { firmInfo, practiceAreas } = useData();


  useEffect(() => {
    getBlogPostSummaries({ language: i18n.language, limit: 3 })
      .then(setRecentPosts)
      .catch(err => {
        console.error("Could not fetch recent posts for footer:", err);
      });
  }, [i18n.language]);


  return (
    <footer className="border-t border-white/10 bg-black/50 glass-effect pt-16 pb-8">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <LocalizedLink to="/" className="flex items-center gap-3 group" aria-label="The Action Firm Home">
              <LogoIcon className="h-10 w-10 transition-transform duration-300 group-hover:scale-110" loading="lazy" />
              <span className="text-sm font-semibold tracking-widest">THE <span className="text-brand-gold-light">ACTION</span> FIRM</span>
            </LocalizedLink>
            <p className="mt-4 text-sm text-white/70 max-w-sm">
              {t('footer.tagline')}
            </p>
            <div className="mt-6 flex items-center gap-4 text-white/80">
              <a href={firmInfo.socials.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-white" aria-label="Facebook"><Facebook size={20}/></a>
              {firmInfo.socials.instagram && <a href={firmInfo.socials.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-white" aria-label="Instagram"><Instagram size={20}/></a>}
              {firmInfo.socials.linkedin && <a href={firmInfo.socials.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-white" aria-label="LinkedIn"><Linkedin size={20}/></a>}
              {firmInfo.socials.youtube && <a href={firmInfo.socials.youtube} target="_blank" rel="noopener noreferrer" className="hover:text-white" aria-label="YouTube"><Youtube size={20}/></a>}
            </div>
          </div>

          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-semibold tracking-wider text-white/90">{t('footer.navigation')}</h3>
              <nav className="mt-4 flex flex-col gap-3 text-sm">
                <LocalizedLink to="/about" className="text-white/70 hover:text-white">{t('footer.aboutUs')}</LocalizedLink>
                <LocalizedLink to="/results" className="text-white/70 hover:text-white">{t('footer.caseResults')}</LocalizedLink>
                <LocalizedLink to="/reviews" className="text-white/70 hover:text-white">{t('footer.testimonials')}</LocalizedLink>
                <LocalizedLink to="/blog" className="text-white/70 hover:text-white">{t('nav.blog')}</LocalizedLink>
                {/* /locations was a second URL rendering the Contact page (duplicate title, description and
                    content); it now 301s to /contact, which already lists the offices and courthouses. */}
                <LocalizedLink to="/contact" className="text-white/70 hover:text-white">{t('nav.locations', 'Locations')}</LocalizedLink>
              </nav>
            </div>
            <div>
              <h3 className="font-semibold tracking-wider text-white/90">{t('footer.practiceAreas')}</h3>
              <nav className="mt-4 flex flex-col gap-3 text-sm">
                {practiceAreas.slice(0, 5).map(area => (
                   <LocalizedLink key={area.slug} to={`/practice-areas/${area.slug}`} className="text-white/70 hover:text-white">{area.title}</LocalizedLink>
                ))}
                 <LocalizedLink to="/practice-areas" className="text-white/70 hover:text-white font-semibold">{t('footer.seeAll')}</LocalizedLink>
              </nav>
            </div>
            <div>
              <h3 className="font-semibold tracking-wider text-white/90">{t('footer.recentPosts')}</h3>
              <nav className="mt-4 flex flex-col gap-3 text-sm">
                {recentPosts.length > 0 ? (
                    recentPosts.map(post => (
                        <LocalizedLink key={post.slug} to={`/blog/${post.slug.replace(/\/$/, '')}`} className="text-white/70 hover:text-white line-clamp-2">
                            {post.title}
                        </LocalizedLink>
                    ))
                ) : (
                    <p className="text-white/70 text-xs">{t('footer.loadingPosts')}</p>
                )}
              </nav>
            </div>
             <div>
              <h3 className="font-semibold tracking-wider text-white/90">{t('footer.contactInfo')}</h3>
               <div className="mt-4 flex flex-col gap-3 text-sm text-white/70">
                <a href={firmInfo.googleMapsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white">
                    {firmInfo.address.streetAddress}<br/>
                    {firmInfo.address.addressLocality}, {firmInfo.address.addressRegion} {firmInfo.address.postalCode}
                </a>
                <a href={`tel:${firmInfo.telephone}`} onClick={() => { trackEvent('click_to_call', { location: 'footer' }); trackConversion('Contact', { content_name: 'click_to_call', location: 'footer' }); }} className="hover:text-white">{firmInfo.displayPhone}</a>
                <a href="mailto:intake@theactionfirm.law" className="hover:text-white">intake@theactionfirm.law</a>
              </div>
            </div>
          </div>
        </div>

        {/* Site Integration Audit item 12: these are real, already-existing
            directory profile URLs (firmInfo.socials) that were never
            rendered anywhere on the site -- consistent citations across
            Google Business Profile, Avvo, Justia, and FindLaw are a real,
            well-documented local-SEO and trust signal. Plain text links,
            not fabricated brand icons -- react-feather has no icons for
            these legal-directory brands. */}
        <div className="mt-12 border-t border-white/10 pt-8 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-xs text-white/70">
          <span className="text-white/70">{t('footer.findUsOn')}</span>
          {firmInfo.socials.googleBusinessProfile && <a href={firmInfo.socials.googleBusinessProfile} target="_blank" rel="noopener noreferrer" className="hover:text-white underline">Google</a>}
          {firmInfo.socials.avvo && <a href={firmInfo.socials.avvo} target="_blank" rel="noopener noreferrer" className="hover:text-white underline">Avvo</a>}
          {firmInfo.socials.justia && <a href={firmInfo.socials.justia} target="_blank" rel="noopener noreferrer" className="hover:text-white underline">Justia</a>}
          {firmInfo.socials.findLaw && <a href={firmInfo.socials.findLaw} target="_blank" rel="noopener noreferrer" className="hover:text-white underline">FindLaw</a>}
        </div>

        <div className="mt-6 border-t border-white/10 pt-8 text-center text-xs text-white/60">
          <p className="mb-4">
            Our attorneys are licensed to practice in all state and federal courts of Utah. Legal representation for matters outside of Utah is subject to proper licensing, pro hac vice admission, or association with local counsel.
          </p>
          <div className="flex justify-center gap-4 mb-4">
            <LocalizedLink to="/privacy-policy" className="hover:text-white underline">Privacy Policy</LocalizedLink>
            {/* The terms text only exists in English; /es/terms-and-conditions served English content under a
                Spanish language tag (hreflang mismatch) and now 301s to the English page, so link there directly. */}
            {i18n.language === 'en'
              ? <LocalizedLink to="/terms-and-conditions" className="hover:text-white underline">Terms and Conditions</LocalizedLink>
              : <a href="/terms-and-conditions/" className="hover:text-white underline">Terms and Conditions</a>}
          </div>
          <p suppressHydrationWarning>{t('footer.disclaimer', { year: currentYear })}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;